import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import { Box, Button, TextField, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

/**
 * The properties for the licenses count component
 */
interface LicensesCountProps {
  /**
   * A state variable that indicates how many licenses the manager is looking to add
   */
  numberOfLicenses: number;
  /**
   * Set the state variable that tracks the number of licenses the manager is looking to add
   * @param _ The number of licenses to set
   */
  setNumberOfLicenses: (_: number) => void;
  /**
   * Whether the outer component is in a loading state
   */
  isLoading: boolean;
  /**
   * This boolean indicates whether the stripe promise is set. If it's not, we'll prevent the user from continuing.
   */
  isStripePromiseSet: boolean;
  /**
   * Handle the action of closing the dialog
   */
  handleClose: () => void;
  /**
   * Once the user has determined the number of licenses they want, this will be called to move them to checkout
   */
  handleCreateCheckoutSession: () => void;
}

/**
 * This component gathers the number of licenses the manager is looking to add to their organization
 */
const LicensesCount = (props: LicensesCountProps) => {
  const maxLicenses = 50000; // Aligns to backend validation
  const minLicenses = 1;

  // State variables
  const [countValidation, setCountValidation] = useState<string | null>(null);

  useEffect(() => {
    setCountValidation(null);
    if (props.numberOfLicenses > maxLicenses)
      setCountValidation(
        `Contact us if you're looking to purchase more than ${maxLicenses} licenses.`
      );
    if (props.numberOfLicenses < minLicenses)
      setCountValidation(`You must add at least ${minLicenses} license.`);
  }, [props.numberOfLicenses]);

  return (
    <Box display="flex" flexDirection="column" gap={2} mt={2}>
      <Typography gutterBottom>
        Enter the number of licenses you&apos;d like to add to your
        organization.
      </Typography>
      <TextField
        label="Number of Licenses"
        type="number"
        inputProps={{ min: 1, max: 50000 }}
        value={props.numberOfLicenses}
        onChange={(e) => props.setNumberOfLicenses(parseInt(e.target.value))}
        error={countValidation !== null}
        helperText={countValidation}
        sx={{ marginBottom: '1em' }}
      />
      <Box display="flex" justifyContent="flex-end" gap={1}>
        <Button
          onClick={props.handleClose}
          disabled={props.isLoading}
          variant="outlined"
          color="secondary"
        >
          Close
        </Button>
        <Tooltip title={countValidation ?? 'Proceed to checkout'} arrow>
          <span
            style={{
              cursor: countValidation != null ? 'help' : 'initial',
            }}
          >
            <Button
              variant="contained"
              onClick={props.handleCreateCheckoutSession}
              disabled={countValidation != null || !props.isStripePromiseSet}
              endIcon={<ArrowForwardIosRoundedIcon />}
            >
              Proceed to Payment
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default LicensesCount;
